import { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { useGetUsersMe } from './queries';
import { RfiFieldName } from '../constants/issue-attribute-names';
import { getAssigneeFromOxygenEntity, getUsersList } from '../helpers/assigned-to-helpers';

import {
  RfiStatus,
  PermittedRfiAttribute,
  PermittedRfiAttributeValues,
  UsersMeResponse,
  WorkflowType,
  PermittedRfiStatus,
  Assignee,
  AssigneeType,
} from '../sdk/types';
import { Oxygen, useOxygen } from '../providers';
import { EMPTY_ARRAY } from '../constants';

export type StatusOption = {
  status: RfiStatus;
  assigneeUsers: Record<Assignee['id'], Assignee>;
  serverStatus: RfiStatus;
  additionalRequiredAttributes: RfiFieldName[]; // validation-helpers.jsL20
  maxAssignees: PermittedRfiStatus['maxAssignees'];
  permittedAttributes: PermittedRfiAttribute[];
};

type GetStatusOptions = (args: {
  usersMe: UsersMeResponse;
  wfType?: WorkflowType;
  oxygen: Oxygen;
}) => StatusOption[];

// Helpers
const getIsAssignedToAttribute = (attribute: PermittedRfiAttribute) =>
  attribute.name === 'assignedTo';
const fixAssigneesSignature = ({
  value,
  type = AssigneeType.User,
}: PermittedRfiAttributeValues) => ({
  id: value,
  type,
});

const getAssignedToUsers = (
  oxygen: Oxygen,
  requiredAttributes: PermittedRfiAttribute[] = [],
): Record<Assignee['id'], Assignee> => {
  const assignees = requiredAttributes
    .find(getIsAssignedToAttribute)
    ?.values?.map(fixAssigneesSignature);

  if (isEmpty(assignees)) {
    return {};
  }

  const oxygenUsers = getUsersList(assignees as Assignee[], oxygen);
  const userAssignees = oxygenUsers.map(getAssigneeFromOxygenEntity);
  return keyBy(userAssignees, 'id');
};

export const getStatusOptions: GetStatusOptions = ({ usersMe, wfType, oxygen }) => {
  let permittedStatuses: PermittedRfiStatus[];
  switch (wfType) {
    case WorkflowType.US:
      permittedStatuses = usersMe?.permittedActions?.createRfi?.permittedStatuses.wfUS ?? [];
      break;
    case WorkflowType.EU:
      permittedStatuses = usersMe?.permittedActions?.createRfi?.permittedStatuses.wfEU ?? [];
      break;
    default:
      permittedStatuses = [];
  }

  const statusOptions: StatusOption[] = permittedStatuses.map((permittedRfiStatus) => {
    const {
      status: serverStatus,
      requiredAttributes,
      maxAssignees,
      permittedAttributes = [],
    } = permittedRfiStatus;
    const permittedAssigneesForStatus =
      permittedRfiStatus.status === 'draft'
        ? {
            [usersMe.user.id]: {
              id: usersMe.user.id,
              type: AssigneeType.User,
            },
          }
        : getAssignedToUsers(oxygen, requiredAttributes);

    const assigneeUsers = permittedAssigneesForStatus ?? {};
    const additionalRequiredAttributes = requiredAttributes.map(({ name }) => name as RfiFieldName);

    return {
      status: serverStatus,
      serverStatus,
      assigneeUsers,
      additionalRequiredAttributes,
      maxAssignees,
      permittedAttributes,
    };
  });

  return statusOptions;
};

export const useStatusOptions = (wfType?: WorkflowType): StatusOption[] => {
  const { data: usersMe } = useGetUsersMe();
  const oxygen = useOxygen();

  return useMemo(() => {
    if (!usersMe || oxygen.loading) {
      return EMPTY_ARRAY;
    }

    return getStatusOptions({ usersMe, wfType, oxygen });
  }, [usersMe, wfType, oxygen]);
};

export default useStatusOptions;
